import { useFormData } from '@/composables/useFormData';
import Comment from '@/models/Comment';
import RatesCost from '@/models/RatesCost';
import RatesCostsService from '@/services/RatesCostsService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';

export const useRatesCostsStore = defineStore('rates_costs', {
  state: () => ({
    costsForm: useFormData({
      costs: ([] as {
        current_quantity: number;
        id: number;
        claim_id: number;
        trade_item_id: number;
        trade_name: string;
        trade_id: number;
      }[]),
    }),
  }),

  getters: {
    models: () => useRepo(RatesCost),
  },

  actions: {
    async create(claimId: Id, tradeItemId: Id, request: ApiRequests['api.v1.subcontractor.claims.lite.rates.store']) {
      const [data, error] = await RatesCostsService.store(claimId, tradeItemId, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async update(id: Id, request: ApiRequests['api.v1.rates-costs.update']) {
      const [data, error] = await RatesCostsService.update(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async storeComment(id: Id, request: ApiRequests['api.v1.rates-costs.comments.store']) {
      const [data, error] = await RatesCostsService.storeComment(id, request);

      if(error) {
        throw error;
      }

      useRepo(Comment).save(data);

      return data;
    },

    async updateComment(ratesCostId: Id, commentId: Id, request: ApiRequests['api.v1.rates-costs.comments.update']) {
      const [data, error] = await RatesCostsService.updateComment(ratesCostId, commentId, request);

      if(error) {
        throw error;
      }

      useRepo(Comment).save(data);

      return data;
    },

    async deleteComment(ratesCostId: Id, commentId: Id) {
      const [data, error] = await RatesCostsService.deleteComment(ratesCostId, commentId);

      if(error) {
        throw error;
      }

      useRepo(Comment).destroy(data.id);

      return data;
    },

    async delete(id: Id) {
      const [data, error] = await RatesCostsService.destroy(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async saveClaimCost(
      claimId: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.subcontractor.claims.rates.claim.store'],
    ) {
      const [data, error] = await RatesCostsService.subcontractor.store(claimId, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },

    async deleteRatesCost(
      claimId: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.subcontractor.claims.rates.claim.destroy'],
    ) {
      const [data, error] = await RatesCostsService.subcontractor.destroy(claimId, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.destroy(data.id);

      return data;
    },

    async saveCertifiedCost(
      projectId: Id,
      claimId: Id,
      id: Id,
      request: ApiRequests['api.v1.subcontractor.claims.rates.certified.store'],
    ) {
      const [data, error] = await RatesCostsService.subcontractor.storeCertified(projectId, claimId, id, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        const comment = data.comment;
        const commentsRepo = useRepo(Comment);

        commentsRepo.save(comment);
      }

      return data;
    },
  },
});
